"use client";

import type { WhiteboardRenderState } from "@/lib/whiteboard/boardState";
import type { WordTilesSpec } from "@/lib/whiteboard/spec";
import { normalizeAnswer } from "@/lib/whiteboard/spec";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Lightbulb, LockKeyhole, RotateCcw } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { WhiteboardWriteInText } from "./WhiteboardWriteInText";
import {
  type WhiteboardCardEventHandler,
  getWhiteboardMarkClass,
  getWhiteboardTargetMark,
  isWhiteboardTargetHighlighted,
  simpleWhiteboardTargetId,
} from "./types";

interface WordTilesCardProps {
  spec: WordTilesSpec;
  renderState: WhiteboardRenderState;
  onEvent?: WhiteboardCardEventHandler;
}

interface Tile {
  id: number;
  text: string;
}

export const WordTilesCard: React.FC<WordTilesCardProps> = ({ spec, renderState, onEvent }) => {
  const allTiles = useMemo<Tile[]>(
    () => spec.tiles.map((text, id) => ({ id, text })),
    [spec.tiles]
  );
  const [placedIds, setPlacedIds] = useState<number[]>([]);
  const [solved, setSolved] = useState(false);
  const [lastWrong, setLastWrong] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const lockedTileIds = useMemo(() => {
    if (renderState.lockedTileCount <= 0) return [];
    const answerTokens = spec.answer
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, renderState.lockedTileCount);
    const used = new Set<number>();
    return answerTokens
      .map((token) => {
        const tile = allTiles.find(
          (candidate) =>
            !used.has(candidate.id) && normalizeAnswer(candidate.text) === normalizeAnswer(token)
        );
        if (!tile) return null;
        used.add(tile.id);
        return tile.id;
      })
      .filter((id): id is number => id !== null);
  }, [allTiles, renderState.lockedTileCount, spec.answer]);
  useEffect(() => {
    if (!lockedTileIds.length) return;
    setPlacedIds((prev) => [...lockedTileIds, ...prev.filter((id) => !lockedTileIds.includes(id))]);
  }, [lockedTileIds]);

  const placedTiles = placedIds
    .map((id) => allTiles.find((t) => t.id === id))
    .filter((t): t is Tile => Boolean(t));
  const poolTiles = allTiles.filter((t) => !placedIds.includes(t.id));
  const allPlaced = placedIds.length === allTiles.length;

  const reportWorkingState = (nextPlacedIds: number[], action = "arranged_tiles") => {
    const built = nextPlacedIds
      .map((id) => allTiles.find((tile) => tile.id === id)?.text)
      .filter((text): text is string => Boolean(text))
      .join(" ");
    onEvent?.({
      event: "working",
      response: built || "(empty)",
      correct: undefined,
      action,
      target: spec.answer,
      targetId: simpleWhiteboardTargetId("word_tiles", "answer"),
      expected: spec.answer,
      progress: `${nextPlacedIds.length}/${allTiles.length} tiles placed`,
      resolved: false,
    });
  };

  const placeTile = (id: number) => {
    if (solved) return;
    setLastWrong(false);
    setPlacedIds((prev) => {
      const next = [...prev, id];
      reportWorkingState(next);
      return next;
    });
  };

  const returnTile = (id: number) => {
    if (solved || lockedTileIds.includes(id)) return;
    setLastWrong(false);
    setPlacedIds((prev) => {
      const next = prev.filter((placedId) => placedId !== id);
      reportWorkingState(next);
      return next;
    });
  };

  const handleCheck = () => {
    if (!allPlaced || solved) return;
    const built = placedTiles.map((t) => t.text).join(" ");
    const correct = normalizeAnswer(built) === normalizeAnswer(spec.answer);
    const nextAttempt = attempt + 1;
    setAttempt(nextAttempt);
    setSolved(correct);
    setLastWrong(!correct);
    onEvent?.({
      event: "answered",
      response: built,
      correct,
      action: "built_sentence",
      target: spec.answer,
      targetId: simpleWhiteboardTargetId("word_tiles", "answer"),
      expected: spec.answer,
      resolved: correct,
      attempt: nextAttempt,
    });
  };

  const handleReset = () => {
    setPlacedIds(lockedTileIds);
    setLastWrong(false);
    reportWorkingState(lockedTileIds, "reset_tiles");
  };

  return (
    <div className="space-y-4">
      {spec.translation && (
        <WhiteboardWriteInText
          as="p"
          text={`"${spec.translation}"`}
          className="text-sm text-[#AEAEB2] italic"
        />
      )}

      {/* Built sentence row */}
      <div
        className={`flex min-h-[3.25rem] flex-wrap items-center gap-2 rounded-xl border px-3 py-2.5 transition-colors duration-200 ${
          solved
            ? "border-[#22C55E]/50 bg-[#22C55E]/10"
            : lastWrong
              ? "border-[#EF4444]/50 bg-[#EF4444]/10"
              : "border-dashed border-white/20 bg-white/[0.03]"
        }`}
      >
        {placedTiles.length === 0 && (
          <WhiteboardWriteInText
            text="Tap the tiles to build the sentence..."
            inline
            initialDelayMs={220}
            className="text-sm text-[#8E8E93]"
          />
        )}
        <AnimatePresence>
          {placedTiles.map((tile) =>
            (() => {
              const tileTargetId = simpleWhiteboardTargetId("word_tiles", "tile", tile.id);
              const mark = getWhiteboardTargetMark(tile.text, renderState, tileTargetId);
              return (
                <motion.button
                  key={tile.id}
                  layout
                  initial={{ opacity: 0, scale: 0.85 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.85 }}
                  onClick={() => returnTile(tile.id)}
                  disabled={solved}
                  data-whiteboard-target-id={tileTargetId}
                  className={`rounded-lg border px-3 py-1.5 text-base font-semibold ${
                    lockedTileIds.includes(tile.id)
                      ? "border-[#FD954F]/60 bg-[#FD954F]/20 text-[#FFD9B8]"
                      : solved
                        ? "border-[#22C55E]/50 bg-[#22C55E]/15 text-[#4ADE80]"
                        : "border-[#FD954F]/40 bg-[#FD954F]/10 text-[#FFD9B8] hover:bg-[#FD954F]/20"
                  } ${getWhiteboardMarkClass(mark)}`}
                  title={mark?.label}
                >
                  {lockedTileIds.includes(tile.id) && (
                    <LockKeyhole size={13} className="mr-1 inline-block -translate-y-0.5" />
                  )}
                  {tile.text}
                </motion.button>
              );
            })()
          )}
        </AnimatePresence>
      </div>

      {/* Tile pool */}
      {!solved && poolTiles.length > 0 && (
        <div className="whiteboard-control-reveal flex flex-wrap gap-2">
          {poolTiles.map((tile) => {
            const tileTargetId = simpleWhiteboardTargetId("word_tiles", "tile", tile.id);
            const isHighlighted = isWhiteboardTargetHighlighted(
              tile.text,
              renderState,
              tileTargetId
            );
            const mark = getWhiteboardTargetMark(tile.text, renderState, tileTargetId);
            return (
              <motion.button
                key={tile.id}
                layout
                whileTap={{ scale: 0.94 }}
                onClick={() => placeTile(tile.id)}
                data-whiteboard-target-id={tileTargetId}
                className={`rounded-lg border px-3 py-1.5 text-base font-semibold transition-colors duration-150 ${
                  isHighlighted
                    ? "whiteboard-tutor-highlight-pulse border-[#FD954F]/80 bg-[#FD954F]/20 text-[#FFD9B8]"
                    : "border-white/15 bg-white/[0.06] text-[#F9F9F9] hover:border-[#FD954F]/60 hover:bg-[#FD954F]/10"
                } ${getWhiteboardMarkClass(mark)}`}
                title={mark?.label}
              >
                {tile.text}
              </motion.button>
            );
          })}
        </div>
      )}

      {spec.hint && !solved && (
        <p className="whiteboard-exercise-hint whiteboard-control-reveal flex items-start gap-1.5 rounded-lg border border-transparent px-2.5 py-2 text-sm leading-relaxed text-[#FD954F]/80">
          <Lightbulb size={15} className="mt-0.5 shrink-0" />
          <span>{spec.hint}</span>
        </p>
      )}

      {/* Actions */}
      {!solved && (
        <div className="whiteboard-control-reveal flex items-center gap-2">
          <button
            onClick={handleCheck}
            disabled={!allPlaced}
            className="flex items-center gap-1.5 rounded-xl bg-[#FD954F] px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#F28747] disabled:bg-[#3A3A3C] disabled:text-[#8E8E93]"
          >
            <Check size={16} />
            Check
          </button>
          {placedIds.length > 0 && (
            <button
              onClick={handleReset}
              className="flex items-center gap-1.5 rounded-xl border border-white/15 px-4 py-2.5 text-sm font-semibold text-[#AEAEB2] transition-colors hover:text-white hover:border-white/30"
            >
              <RotateCcw size={15} />
              Reset
            </button>
          )}
          {lastWrong && (
            <span className="text-sm font-medium text-[#FCA5A5]">
              Not quite — tap a tile to move it.
            </span>
          )}
        </div>
      )}

      {solved && (
        <motion.p
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-sm font-semibold text-[#4ADE80]"
        >
          Nice — that&apos;s the sentence.
        </motion.p>
      )}
    </div>
  );
};
